import type { EnforcedKeys } from "@suse-o11y/enforced-keys";
import type { JSON } from "@suse-o11y/json";
import { Lazy } from "@suse-o11y/lazy";
import type { PickReadonly } from "@suse-o11y/pick-readonly";
import { prop } from "@suse-o11y/prop";
import { Template } from "@suse-o11y/template";
import { toJSON } from "@suse-o11y/to-json";
import { z } from "zod";

import type { PersesPanel } from "../openapi";

import { DashboardPluginSpec } from "./_dashboard-plugin-spec";
import type { DashboardWidgetFormat } from "./_dashboard-widget-format";

export type DashboardWidgetSpecConfig<TPlugin extends DashboardPluginSpec = DashboardPluginSpec> = PickReadonly<
	Omit<DashboardWidgetSpec<TPlugin>, "update" | "isEqual">
>;

export type DashboardWidgetSpecInitConfig<TPlugin extends DashboardPluginSpec = DashboardPluginSpec> = EnforcedKeys<
	DashboardWidgetSpecConfig<TPlugin>,
	"plugin"
>;

export class DashboardWidgetSpec<TPlugin extends DashboardPluginSpec = DashboardPluginSpec> {
	public static schema = z
		.object({
			display: z.object({
				name: z
					.string()
					.catch("")
					.transform((query) => Template<null>(query)),
				description: z
					.string()
					.catch("")
					.transform((query) => Template<null>(query)),
			}),
		})
		.transform(prop("display"))
		.catch({ name: Template(""), description: Template("") })
		.and(DashboardPluginSpec.schema)
		.transform(({ name, description, ...plugin }) => new DashboardWidgetSpec(name, description, plugin));

	public static of<TPlugin extends DashboardPluginSpec>({
		name,
		description,
		plugin,
	}: DashboardWidgetSpecInitConfig<TPlugin>): DashboardWidgetSpec<TPlugin> {
		const spec = DashboardPluginSpec.toPerses(plugin);

		const fromPerses = DashboardWidgetSpec.schema.safeParse({
			display: {
				name: name?.source,
				description: description?.source,
			} satisfies JSON,
			...spec,
		});

		const withFallback =
			fromPerses.data ??
			new DashboardWidgetSpec(name ?? Template(""), description ?? Template(""), {
				kind: plugin.kind,
				spec: {},
				queries: [],
				links: [],
			});

		return withFallback as DashboardWidgetSpec<TPlugin>;
	}

	private constructor(
		public readonly name: Template<["${", "}"], null>,
		public readonly description: Template<["${", "}"], null>,
		public readonly plugin: TPlugin,
	) {}

	private readonly hash = Lazy.create(() => toJSON(this.toPerses()));

	private readonly perses = Lazy.create((): PersesPanel => {
		const spec = DashboardPluginSpec.toPerses(this.plugin);

		return {
			spec: {
				...spec,
				display: {
					name: this.name.source,
					description: this.description.source,
				} satisfies JSON,
			},
		};
	});

	/**
	 * An alias for extracting the unit from the DashboardPluginSpec union.
	 */
	public get format(): undefined | DashboardWidgetFormat {
		if ("yAxis" in this.plugin.spec) {
			return this.plugin.spec.yAxis?.format;
		}

		if ("format" in this.plugin.spec) {
			return this.plugin.spec.format;
		}

		return undefined;
	}

	public isEqual(another: DashboardWidgetSpec<TPlugin>): boolean {
		return this === another || this.hash.get() === another.hash.get();
	}

	public toPerses(): PersesPanel {
		return this.perses.get();
	}
}
